import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Fingerprint } from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '../auth/AuthProvider.jsx';
import { passkeysApi } from '../api/passkeys.js';
import { ApiError } from '../api/client.js';
import { Button } from '../components/ui/Button.jsx';
import { Input } from '../components/ui/Input.jsx';
import { env } from '../lib/env.js';
import { t } from '../lib/i18n.js';

export function MfaChallengePage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};
  const methods = prefill.methods || ['totp'];
  const supportsPasskey = methods.includes('passkey') && browserSupportsWebAuthn();
  const supportsTotp = methods.includes('totp');

  // Prefer the passkey: it is stronger and frictionless. TOTP is the fallback.
  const [mode, setMode] = useState(supportsPasskey ? 'passkey' : 'totp');
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { mfaCode: '' }
  });

  if (!prefill.email || !prefill.password) {
    return <Navigate to="/login" replace />;
  }

  const onSubmitTotp = async (values) => {
    setSubmitting(true);
    try {
      await login({ email: prefill.email, password: prefill.password, mfaCode: values.mfaCode.trim() });
      navigate(prefill.from || '/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'MFA_INVALID' || err.code === 'MFA_REQUIRED')) {
        toast.error(t('mfa.error.invalid'));
        return;
      }
      toast.error(t('login.error.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitPasskey = async () => {
    setSubmitting(true);
    try {
      const options = await passkeysApi.loginOptions({ email: prefill.email, password: prefill.password });
      const response = await startAuthentication({ optionsJSON: options });
      await login({ email: prefill.email, password: prefill.password, passkeyResponse: response });
      navigate(prefill.from || '/', { replace: true });
    } catch (err) {
      if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        toast.error(t('login.passkey.cancelled'));
        return;
      }
      if (err instanceof ApiError) {
        if (err.code === 'MFA_INVALID') {
          toast.error(t('login.passkey.failed'));
          return;
        }
        toast.error(err.message || t('login.error.generic'));
        return;
      }
      toast.error(t('login.error.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const tabClass = (active) =>
    `flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${active ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'}`;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-4 py-10">
      <div className="w-full max-w-[380px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-surface-elevated shadow-card">
            <img src="/favicon.png" alt={env.appName} className="h-7 w-7" />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('mfa.title')}</h1>
          <p className="mt-1.5 text-sm text-ink-muted">Conferma la tua identità per continuare.</p>
        </div>

        <div className="rounded-2xl border border-border bg-surface-elevated p-6 shadow-card sm:p-7">
          {(supportsTotp && supportsPasskey) && (
            <div className="mb-5 flex rounded-xl border border-border bg-surface p-1">
              <button type="button" className={tabClass(mode === 'passkey')} onClick={() => setMode('passkey')}>
                Passkey
              </button>
              <button type="button" className={tabClass(mode === 'totp')} onClick={() => setMode('totp')}>
                Codice app
              </button>
            </div>
          )}

          {mode === 'passkey' ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-ink-muted">
                Conferma con la biometria del dispositivo o la chiave di sicurezza.
              </p>
              <Button onClick={onSubmitPasskey} loading={submitting} className="w-full">
                {!submitting && <Fingerprint className="h-4 w-4" />}
                {submitting ? t('login.passkey.busy') : t('login.passkey')}
              </Button>
            </div>
          ) : (
            <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmitTotp)} noValidate>
              <Input
                label={t('mfa.code')}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={8}
                error={errors.mfaCode && 'Codice richiesto'}
                {...register('mfaCode', { required: true, minLength: 6 })}
              />
              <Button type="submit" loading={submitting} className="w-full">
                {submitting ? t('mfa.submitting') : t('mfa.submit')}
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-ink-faint">{env.appName} &middot; area riservata</p>
      </div>
    </div>
  );
}
